// Seasonal looks for the terrain, grass and tree scatter.
//
// The calendar (sim/gameTime.ts) owns the boundaries — four seasons of 91
// days; this module owns what each season does to instance colours. Every
// transform is a small HSL nudge plus an optional mix toward a "frost"
// colour (winter), applied in the same default working space the scatter's
// jitterColor() uses, so season tint composes with the existing palette
// walk + jitter instead of fighting it.
//
// Transitions: at a season's start the look blends from the previous
// season over SEASON_TRANSITION_DAYS (smoothstepped), so the world slides
// into winter instead of snapping.

import * as THREE from 'three';
import { DAYS_PER_SEASON, SEASON_NAMES, type Season } from '../sim/gameTime.js';
import type { ScatterPropType } from './TerrainScatterMaterials.js';

/** Days over which a new season's look fades in. */
export const SEASON_TRANSITION_DAYS = 6;

/**
 * One seasonal look: a hue add, saturation/lightness multipliers, and how
 * far to mix toward the domain's frost colour (0..1; 0 outside winter).
 */
export interface SeasonTint {
  readonly h: number;
  readonly s: number;
  readonly l: number;
  readonly frost: number;
}

const T = (h: number, s: number, l: number, frost = 0): SeasonTint => ({ h, s, l, frost });

/** Ground tiles (the vegetated biomes; others take the tint at reduced weight). */
const GROUND_TINTS: Record<Season, SeasonTint> = {
  spring: T(0.015, 1.12, 1.0),
  summer: T(0.0, 1.05, 1.03),
  autumn: T(-0.035, 1.0, 0.97),
  winter: T(0.0, 0.45, 1.12, 0.25),
};

/** Grass tufts — taller and vivid in summer, pale stubble in winter. */
const GRASS_TINTS: Record<Season, SeasonTint> = {
  spring: T(0.02, 1.15, 1.05),
  summer: T(0.005, 1.1, 1.0),
  autumn: T(-0.05, 0.9, 1.0),
  winter: T(0.0, 0.35, 1.05, 0.55),
};

/** Deciduous crowns (oak, birch, bush) — the autumn turn, the winter strip. */
const DECIDUOUS_TINTS: Record<Season, SeasonTint> = {
  spring: T(0.02, 1.1, 1.05),
  summer: T(0.0, 1.0, 1.0),
  autumn: T(-0.09, 1.15, 0.95),
  winter: T(0.0, 0.3, 1.0, 0.6),
};

/** Conifers barely change; winter dusts them with snow. */
const CONIFER_TINTS: Record<Season, SeasonTint> = {
  spring: T(0.0, 1.0, 1.0),
  summer: T(0.0, 1.0, 1.0),
  autumn: T(0.0, 1.0, 1.0),
  winter: T(0.0, 0.85, 1.0, 0.3),
};

/** Mix targets per domain — what "frost" blends toward. */
export const FROST_COLORS = {
  ground: '#dfe6e2',
  grass: '#e8eef0',
  deciduous: '#cfd8da',
  conifer: '#dfe8ec',
} as const;

const DECIDUOUS = new Set<ScatterPropType>(['oak', 'birch', 'bush']);
const CONIFERS = new Set<ScatterPropType>(['pine', 'spruce']);

export type FoliageClass = 'deciduous' | 'conifer' | null;

/** Which seasonal table a scatter prop type follows (null = season-blind). */
export function foliageClassOf(propType: ScatterPropType): FoliageClass {
  if (DECIDUOUS.has(propType)) return 'deciduous';
  if (CONIFERS.has(propType)) return 'conifer';
  return null;
}

export interface SeasonPhase {
  readonly season: Season;
  /** The season this one fades into at its tail (wraps winter → spring). */
  readonly next: Season;
  /** 0 → fully this season's look, 1 → fully the next season's. */
  readonly t: number;
}

/**
 * Calendar position with the transition window resolved. The fade sits at
 * the *tail* of each season (not the head), so a fresh match on day 1 opens
 * in full spring rather than fading out of winter. The +3-day shift keeps
 * the wrap (winter → spring, days ~356–361) clear of the year boundary.
 */
export function seasonPhase(day: number): SeasonPhase {
  const d = Math.max(1, Math.floor(day));
  const p = (d - 1 + 3) % (DAYS_PER_SEASON * 4);
  const idx = Math.min(3, Math.floor(p / DAYS_PER_SEASON));
  const within = p % DAYS_PER_SEASON;
  const fadeStart = DAYS_PER_SEASON - SEASON_TRANSITION_DAYS;
  const raw = within >= fadeStart ? (within - fadeStart) / SEASON_TRANSITION_DAYS : 0;
  const t = raw * raw * (3 - 2 * raw); // smoothstep
  const season = SEASON_NAMES[idx]!;
  return { season, next: SEASON_NAMES[(idx + 1) % 4]!, t };
}

/** Linear blend of two tints. */
export function blendTints(a: SeasonTint, b: SeasonTint, t: number): SeasonTint {
  return {
    h: a.h + (b.h - a.h) * t,
    s: a.s + (b.s - a.s) * t,
    l: a.l + (b.l - a.l) * t,
    frost: a.frost + (b.frost - a.frost) * t,
  };
}

/** Blended ground tint for a day of year. */
/** Pin the season by climate (terrain-levels D11): arctic maps live in
 * mid-winter, tropical/desert in high summer; temperate follows the clock. */
export function climateAdjustedDay(climate: string | null | undefined, day: number): number {
  const d = Math.max(1, Math.floor(day));
  switch (climate) {
    case 'arctic':
      return 274 + ((d - 1) % 45); // deep winter
    case 'tropical':
      return 92 + ((d - 1) % 45); // high summer
    case 'desert':
      return 138 + ((d - 1) % 45); // scorching summer
    default:
      return d;
  }
}

export function groundSeasonTint(day: number): SeasonTint {
  const { season, next, t } = seasonPhase(day);
  return blendTints(GROUND_TINTS[season]!, GROUND_TINTS[next]!, t);
}

/** Blended grass-tuft tint for a day of year. */
export function grassSeasonTint(day: number): SeasonTint {
  const { season, next, t } = seasonPhase(day);
  return blendTints(GRASS_TINTS[season]!, GRASS_TINTS[next]!, t);
}

/** Blended foliage tint for a day of year, or null for season-blind props. */
export function foliageSeasonTint(day: number, propType: ScatterPropType): SeasonTint | null {
  const cls = foliageClassOf(propType);
  if (cls === null) return null;
  const { season, next, t } = seasonPhase(day);
  const table = cls === 'deciduous' ? DECIDUOUS_TINTS : CONIFER_TINTS;
  return blendTints(table[season]!, table[next]!, t);
}

// Module-level singleton — see TerrainTiles/TerrainScatter for the pattern.
const _hsl = { h: 0, s: 0, l: 0 };

const FROST_CACHE = new Map<string, THREE.Color>();

function frostColor(hex: string): THREE.Color {
  let c = FROST_CACHE.get(hex);
  if (!c) {
    c = new THREE.Color(hex);
    FROST_CACHE.set(hex, c);
  }
  return c;
}

/**
 * Apply a season tint in place: hue/sat/light nudge first, then a RGB lerp
 * toward the domain's frost colour (winter pales *and* desaturates with one
 * tint). Null tint = untouched.
 */
export function applySeasonTint(
  color: THREE.Color,
  tint: SeasonTint | null,
  frostHex: string,
): THREE.Color {
  if (tint === null) return color;
  color.getHSL(_hsl);
  color.setHSL(
    (_hsl.h + tint.h + 1) % 1,
    Math.min(1, Math.max(0, _hsl.s * tint.s)),
    Math.min(1, Math.max(0, _hsl.l * tint.l)),
  );
  if (tint.frost > 0) color.lerp(frostColor(frostHex), tint.frost);
  return color;
}
